import {
	useInfiniteQuery,
	useQuery,
	useQueryClient,
} from '@tanstack/react-query';
import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import EmptyIcon from '@/assets/icons/empty.svg';
import { Button } from '@/components/atoms/Button';
import QueryWrapper from '@/components/molecules/QueryWrapper';
import ApprovalFlowModal from '@/components/organisms/ApprovalFlowModal';
import { ApprovalFlowData } from '@/components/organisms/ApprovalFlowModal/types';
import InfiniteTable from '@/components/organisms/InfiniteTable';
import {
	approvalFlowListSchema,
	approvalFlowSchema,
} from '@/constants/ApprovalConstants';
import {
	ExtraTripsApprovalFlow,
	getColumns,
} from '@/constants/tableColumns/ExtraTripsApprovalFlowsColumns';
import api from '@/services/apiSgft';

function toModalData(
	flow: ReturnType<typeof approvalFlowSchema.parse>,
): ApprovalFlowData {
	return {
		id: flow.id,
		companyId: flow.company.id,
		boardId: flow.board.id,
		managementId: flow.management.id,
		type: flow.type,
		requesters: flow.requesters,
		approvalSteps: flow.approvalSteps,
	};
}

export default function ExtraTripsApprovalFlows() {
	const { flowId } = useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const flowIdNumber = Number(flowId);
	const hasFlowId = Number.isInteger(flowIdNumber) && flowIdNumber > 0;

	const fetchApprovalFlows = async ({ pageParam = 1 }) => {
		try {
			const response = await api.get('approval/flow', {
				params: {
					page: pageParam,
					size: 20,
				},
			});
			const parsedData = approvalFlowListSchema.parse(response.data);
			return parsedData;
		} catch (e) {
			toast.error('Erro ao carregar os fluxos de aprovação', {
				theme: 'colored',
				toastId: 'error',
			});
			throw e;
		}
	};

	const {
		data,
		isLoading,
		fetchNextPage,
		isFetchingNextPage,
		hasNextPage,
		isError,
	} = useInfiniteQuery(['extra-trips-approval-flows'], fetchApprovalFlows, {
		getNextPageParam: (lastPage) => lastPage.nextPage,
	});

	const { data: editingApprovalFlow } = useQuery(
		['approval-flow', flowIdNumber],
		async () => {
			const response = await api.get(`approval/flow/${flowIdNumber}`);
			return approvalFlowSchema.parse(response.data);
		},
		{
			enabled: hasFlowId,
			retry: false,
			onError: () => {
				if (queryClient.getQueryData(['approval-flow', flowIdNumber])) return;
				toast.error('Erro ao carregar o fluxo de aprovação', {
					theme: 'colored',
					toastId: 'approval-flow-load-error',
				});
			},
		},
	);

	const approvalFlows = React.useMemo(() => {
		const flows = data?.pages.flatMap((page) => page.approvalFlows) || [];
		return flows.map((flow) => ({
			...flow,
			subRows:
				flow.approvalSteps && flow.approvalSteps.length > 0
					? [
							{
								id: `${flow.id}-subrow`,
								isSubRow: true,
								parentId: flow.id,
								approvalSteps: flow.approvalSteps,
							},
						]
					: [],
		}));
	}, [data]);

	const [isCreating, setIsCreating] = React.useState(false);

	React.useEffect(() => {
		if (!flowId || hasFlowId) return;
		toast.error('Fluxo de aprovação inválido', {
			theme: 'colored',
			toastId: 'approval-flow-invalid-id',
		});
	}, [flowId, hasFlowId]);

	const handleEdit = React.useCallback(
		(approvalFlow: ExtraTripsApprovalFlow) => {
			setIsCreating(false);
			const parsed = approvalFlowSchema.safeParse(approvalFlow);
			if (parsed.success) {
				queryClient.setQueryData(
					['approval-flow', approvalFlow.id],
					parsed.data,
				);
			}
			navigate(`/home/fluxos-de-aprovacao/${approvalFlow.id}`);
		},
		[navigate, queryClient],
	);

	const closeModal = () => {
		setIsCreating(false);
		if (flowId) {
			navigate('/home/fluxos-de-aprovacao');
		}
	};

	const columns = React.useMemo(() => getColumns(handleEdit), [handleEdit]);

	return (
		<div className="relative flex h-full max-h-screen w-full flex-col items-start">
			<div className="mt-6 flex w-full justify-between">
				<div className="mb-2 pr-3 font-graphie text-2xl font-bold text-primary">
					Fluxos de Aprovação
				</div>
				<Button
					className="h-7 w-40 rounded-full text-xs text-white"
					onClick={() => {
						setIsCreating(true);
						if (flowId) {
							navigate('/home/fluxos-de-aprovacao');
						}
					}}
				>
					+ Novo Cadastro
				</Button>
			</div>
			<div className="mt-4 w-full rounded-md bg-white px-4 py-4">
				<QueryWrapper isLoading={isLoading} isError={isError}>
					{approvalFlows?.length ? (
						<InfiniteTable
							hasNextPage={hasNextPage}
							data={approvalFlows}
							columns={columns}
							fetchNextPage={fetchNextPage}
							isFetchingNextPage={isFetchingNextPage}
							alignTextLeft
							hasConsistentBackground
							getRowClassName={(row: any) => {
								if (row.isSubRow) {
									return 'border-t border-b border-[#E2E9FF] bg-gray-100';
								}
								return 'border-t border-b border-[#E2E9FF] bg-white';
							}}
							isWhiteTable
						/>
					) : (
						<div className="flex h-full w-full flex-col items-center justify-center">
							<img src={EmptyIcon} className="w-40" alt="Vazio" />
							<div className="w-full text-center text-sm">
								<span className="font-semibold text-blue-800">
									Nenhum fluxo de aprovação foi encontrado
								</span>
							</div>
						</div>
					)}
				</QueryWrapper>
			</div>
			{(isCreating || (hasFlowId && editingApprovalFlow)) && (
				<ApprovalFlowModal
					isOpen
					onClose={closeModal}
					data={
						!isCreating && editingApprovalFlow
							? toModalData(editingApprovalFlow)
							: undefined
					}
				/>
			)}
		</div>
	);
}
